import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProtectedImage from './ProtectedImage';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';

function VehicleCard({ vehicle }) {
  const { client } = useAuth();
  const { favoriteIds, toggle } = useFavorites();
  const navigate = useNavigate();
  const [favLoading, setFavLoading] = useState(false);

  const isFav = favoriteIds.includes(vehicle.id);

  const reduction = vehicle.prixPromo && vehicle.prix
    ? Math.round(((vehicle.prix - vehicle.prixPromo) / vehicle.prix) * 100)
    : null;

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!client) {
      navigate('/connexion', {
        state: { from: window.location.pathname, message: 'Connectez-vous pour sauvegarder des véhicules en favoris.' }
      });
      return;
    }

    setFavLoading(true);
    try {
      await toggle(vehicle.id);
    } catch (err) {
      console.error(err);
    } finally {
      setFavLoading(false);
    }
  };

  return (
    <Link to={`/vehicle/${vehicle.id}`} onClick={() => sessionStorage.setItem('scrollToVehicle', vehicle.id)}>
      <div
        className="relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
        style={{ height: '380px' }}
      >
        {vehicle.mainImageUrl ? (
          <ProtectedImage
            src={vehicle.mainImageUrl}
            alt={`${vehicle.marque} ${vehicle.modele}`}
            className="w-full h-full transition-transform duration-500 group-hover:scale-105"
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center text-6xl">
            🚗
          </div>
        )}

        {/* Bouton favori */}
        <button
          onClick={handleFavoriteClick}
          disabled={favLoading}
          className={`absolute top-3 right-3 z-20 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 ${
            isFav
              ? 'bg-red-500 text-white scale-110'
              : 'bg-white/90 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:scale-110'
          } ${favLoading ? 'opacity-50 cursor-wait' : ''}`}
          title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <svg
            viewBox="0 0 24 24"
            fill={isFav ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={2}
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>

        {/* Badges haut gauche */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
          {vehicle.enVedette && (
            <span className="bg-yellow-500 text-black text-xs font-extrabold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
              ⭐ EN VEDETTE
            </span>
          )}
          {vehicle.prixPromo && vehicle.statut !== 'VENDU' && (
            <span className="bg-red-500 text-white text-xs font-extrabold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
              🔥 PROMO -{reduction}%
            </span>
          )}
          {vehicle.disponibilite === 'ARRIVAGE' && vehicle.statut !== 'VENDU' && (
            <span className="bg-blue-500 text-white text-xs font-extrabold px-3 py-1 rounded-full shadow-lg">
              🚢 En arrivage
            </span>
          )}
        </div>

        {vehicle.statut === 'VENDU' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
            <span className="bg-yellow-500 text-black text-2xl font-extrabold px-8 py-4 rounded-2xl transform -rotate-12 shadow-xl">VENDU</span>
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-5">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              vehicle.etat === 'Neuf' ? 'bg-yellow-500 text-black' : 'bg-white/20 text-white'
            }`}>
              {vehicle.etat}
            </span>
            {vehicle.disponibilite && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                vehicle.disponibilite === 'IMMEDIAT'
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-500 text-white'
              }`}>
                {vehicle.disponibilite === 'IMMEDIAT' ? 'Disponible' : 'En arrivage'}
              </span>
            )}
            {reduction && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                -{reduction}%
              </span>
            )}
          </div>

          <h3 className="text-white font-bold text-xl">
            {vehicle.marque} {vehicle.modele}
          </h3>
          <p className="text-gray-300 text-sm">
            {vehicle.dateMiseEnCirculation} • {vehicle.kilometrage ? vehicle.kilometrage.toLocaleString() + ' km' : ''} • {vehicle.carburant}
          </p>

          {vehicle.statut !== 'VENDU' && (
            <div className="mt-1">
              {vehicle.prix && (
                vehicle.prixPromo ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-yellow-400 font-bold text-xl">{vehicle.prixPromo.toLocaleString()} € HT</span>
                    <span className="text-gray-400 text-sm line-through">{vehicle.prix.toLocaleString()} €</span>
                  </div>
                ) : (
                  <p className="text-yellow-400 font-bold text-xl">{vehicle.prix.toLocaleString()} € HT</p>
                )
              )}
              {vehicle.prixTtc && (
                vehicle.prixPromoTtc ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gray-300 text-sm">{vehicle.prixPromoTtc.toLocaleString()} € TTC</span>
                    <span className="text-gray-500 text-xs line-through">{vehicle.prixTtc.toLocaleString()} €</span>
                  </div>
                ) : (
                  <p className="text-gray-300 text-sm">{vehicle.prixTtc.toLocaleString()} € TTC</p>
                )
              )}
            </div>
          )}
        </div>

        <div className="absolute bottom-4 right-14 opacity-0 group-hover:opacity-100 transition-all duration-300">
          <span className="bg-yellow-500 text-black px-4 py-2 rounded-full font-bold text-sm">
            Voir
          </span>
        </div>
      </div>
    </Link>
  );
}

export default VehicleCard;
